import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useRecoilValue } from "recoil";
import { DateTime } from "luxon";
import { Box, MenuItem, TextField, Theme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { makeStyles } from "tss-react/mui";
import CustomDatePicker from "../../components/Inputs/DatePickers/CustomDatePicker";

import { Module } from "../../services/Module";
import { Column } from "../../Layout/utils";
import { DateOptions } from "../../services/Alert";
import { TooltipLabel } from "../../components/Tooltips";
import { formInputsStyles } from "../../Configuration/styles";
import { currentUserAtom } from "../../AuthProvider";

export enum MODE {
  CUSTOM = "custom",
  CALENDAR_YEAR = "calendar-year",
  NEXT_CALENDAR_YEAR = "next-calendar-year",
  // Full previous calendar year — Jan 1 to Dec 31 of `today.year - 1`.
  // Distinct from `YEAR` (which is a rolling-forward 12-month window).
  LAST_CALENDAR_YEAR = "last-calendar-year",
  MONTH = "month",
  WEEK = "week",
  YEAR = "year",
  // Rolling window from today to today + the client's max lead time
  // (the `lead_time_min` constant, stored as a negative "days before stay").
  LEAD_TIME = "lead-time",
}

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    minWidth: "280px",
  },
}));

const ErrorMessage = styled("span")(({ theme }: { theme: Theme }) => ({
  fontSize: "0.9rem",
  color: theme.vars.palette.error.main,
}));

const useGetModes = (leadTimeDays: number, selectedMode: MODE) => {
  const intl = useIntl();

  return useMemo(() => {
    // Hide the lead-time range from the default choices when it's within a year
    // (364-366 days), since it would just duplicate "Rolling year" — using a
    // range rather than an exact match so it doesn't reappear on bissextile
    // years. Still keep it when it's the currently selected mode (e.g. restored
    // from a saved filter), so MUI's <Select> never gets an out-of-range value.
    const isAboutOneYear = leadTimeDays >= 364 && leadTimeDays <= 366;
    const showLeadTime = !isAboutOneYear || selectedMode === MODE.LEAD_TIME;
    return [
      { name: MODE.CUSTOM, label: intl.formatMessage({ id: "CUSTOM" }) },
      { name: MODE.WEEK, label: intl.formatMessage({ id: "ROLLING_WEEK" }) },
      { name: MODE.MONTH, label: intl.formatMessage({ id: "ROLLING_MONTH" }) },
      { name: MODE.YEAR, label: intl.formatMessage({ id: "ROLLING_YEAR" }) },
      ...(showLeadTime
        ? [
            {
              name: MODE.LEAD_TIME,
              label: intl.formatMessage(
                { id: "ROLLING_LEAD_TIME" },
                { days: leadTimeDays }
              ),
            },
          ]
        : []),
      {
        name: MODE.CALENDAR_YEAR,
        label: intl.formatMessage({ id: MODE.CALENDAR_YEAR }),
      },
      {
        name: MODE.LAST_CALENDAR_YEAR,
        label: intl.formatMessage({ id: MODE.LAST_CALENDAR_YEAR }),
      },
      {
        name: MODE.NEXT_CALENDAR_YEAR,
        label: intl.formatMessage({ id: MODE.NEXT_CALENDAR_YEAR }),
      },
    ];
  }, [intl, leadTimeDays, selectedMode]);
};

const DEFAULT_MODE = MODE.MONTH;

const getNonCustomDates = (mode: MODE, leadTimeDays = 365) => {
  const params: any = {};
  let start;
  if (mode === MODE.LEAD_TIME) {
    start = DateTime.now().startOf("day");
    // user input that must be included, so no day subtraction (like week/month/year)
    return { start, end: start.plus({ days: leadTimeDays }).startOf("day") };
  } else if (mode === MODE.CALENDAR_YEAR) {
    params.years = 1;
    start = DateTime.now().startOf("year");
  } else if (mode === MODE.NEXT_CALENDAR_YEAR) {
    params.years = 1;
    start = DateTime.now().startOf("year").plus(params);
  } else if (mode === MODE.LAST_CALENDAR_YEAR) {
    params.years = 1;
    start = DateTime.now().startOf("year").minus(params);
  } else {
    params[`${mode}s`] = 1;
    start = DateTime.now().startOf("day");
    // We return the end date without subtracting a day
    // because it's a user input that must be included
    return { start, end: start.plus(params).startOf("day") };
  }
  // we subtract one day because the backend include the end date
  return { start, end: start.plus(params).startOf("day").minus({ days: 1 }) };
};

export const getDates = (options: DateOptions) => {
  if (options.type === MODE.CUSTOM) {
    return {
      start: options.start
        ? DateTime.fromISO(options.start)
        : DateTime.now().startOf("day"),
      end: options.end
        ? DateTime.fromISO(options.end)
        : DateTime.now().startOf("day").plus({ days: 1 }),
    };
  }
  if (options.type === MODE.LEAD_TIME) {
    return getNonCustomDates(MODE.LEAD_TIME, options.days);
  }
  return getNonCustomDates(options.type);
};

export const CreateFilterDateForm = ({
  data,
  onSubmit,
  moduleSlug,
}: {
  data: DateOptions;
  onSubmit: (data: DateOptions) => void;
  moduleSlug: Module;
}) => {
  const { classes } = useStyles();
  const inputStyles = formInputsStyles();
  const intl = useIntl();
  const [selectedMode, setSelectedMode] = useState<MODE>(DEFAULT_MODE);
  const [startDate, setStartDate] = useState<DateTime | null>(null);
  const [endDate, setEndDate] = useState<DateTime | null>(null);
  const [isDurationValid, setIsDurationValid] = useState<boolean>(true);
  const currentUser = useRecoilValue(currentUserAtom);
  const [leadTimeMin, setLeadTimeMin] = useState<number>(366);
  // How far into the future the LEAD_TIME range reaches. `lead_time_min` is
  // stored as a negative "days before stay", so we negate it to get a positive
  // day count; fall back to 366 days (matching the `leadTimeMin` validation
  // default, which allows a full bissextile year) when no lead time is set.
  const leadTimeDays = currentUser?.tenant.flags.lead_time_min
    ? -currentUser.tenant.flags.lead_time_min
    : 366;
  const modes = useGetModes(leadTimeDays, selectedMode);

  useEffect(() => {
    if (moduleSlug === Module.EVENT) {
      const endOfYear = DateTime.now().endOf("year");
      const startDateLimit = endOfYear
        .minus({ years: currentUser?.tenant.flags.event_view_depth_in_years })
        .startOf("year");
      const diffDays = endOfYear.diff(startDateLimit, "days").days;
      setLeadTimeMin(Math.floor(diffDays));
    } else if (currentUser?.tenant.flags.lead_time_min) {
      setLeadTimeMin(-currentUser.tenant.flags.lead_time_min);
    }
  }, [currentUser, leadTimeMin, moduleSlug]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const mode = event.target.value as MODE;
    setSelectedMode(mode);
    if (mode !== MODE.CUSTOM) {
      const { start, end } = getNonCustomDates(mode, leadTimeDays);
      setStartDate(start);
      setEndDate(end);
    }
  };

  const handleSubmit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    if (isDurationValid) {
      const params: any = { type: selectedMode };
      if (params.type === MODE.CUSTOM) {
        params.start = startDate?.toISODate();
        params.end = endDate?.toISODate();
      } else if (params.type === MODE.LEAD_TIME) {
        params.days = leadTimeDays;
      }
      onSubmit(params);
    }
  };

  useEffect(() => {
    setSelectedMode(data.type);
    const { start, end } = getDates(data);
    setStartDate(start);
    setEndDate(end);
  }, [data]);

  useEffect(() => {
    let diffDays;

    if (endDate && startDate) {
      diffDays = endDate?.diff(startDate, "days").toObject();
    }

    if (diffDays?.days) {
      diffDays.days <= leadTimeMin
        ? setIsDurationValid(true)
        : setIsDurationValid(false);
    }
  }, [startDate, endDate, leadTimeMin]);

  return (
    <form
      id="create-filter-form"
      className={classes.root}
      autoComplete="off"
      onSubmit={handleSubmit}
    >
      <Column
        className={inputStyles.classes.root}
        sx={{
          gap: 1,
        }}
      >
        <Box
          className={`${inputStyles.classes.container} ${inputStyles.classes.inputSize}`}
        >
          <TextField
            className={inputStyles.classes.inputText}
            variant="outlined"
            id="standard-select-date"
            fullWidth
            label={intl.formatMessage({ id: "SELECT_DATE_MODE" })}
            onChange={handleChange}
            select
            value={selectedMode}
          >
            {modes.map((option) => (
              <MenuItem key={option.name} value={option.name}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box
          className={`${inputStyles.classes.container} ${inputStyles.classes.inputSize}`}
        >
          <CustomDatePicker
            label="DATE_START"
            maxDate={endDate ?? undefined}
            value={startDate}
            onChange={(isoStr) => {
              setSelectedMode(MODE.CUSTOM);
              setStartDate(isoStr ? DateTime.fromISO(isoStr) : null);
            }}
          />
        </Box>
        <Box
          className={`${inputStyles.classes.container} ${inputStyles.classes.inputSize}`}
        >
          <CustomDatePicker
            label={
              <TooltipLabel
                sxStyles={{ fontWeight: 700 }}
                label={intl.formatMessage({ id: "DATE_END" })}
                tooltipLabel={intl.formatMessage({ id: "DATE_INCLUDED" })}
                translateTooltip={false}
              />
            }
            translateLabel={false}
            minDate={startDate ?? undefined}
            value={endDate}
            onChange={(isoStr) => {
              setSelectedMode(MODE.CUSTOM);
              setEndDate(isoStr ? DateTime.fromISO(isoStr) : null);
            }}
          />
        </Box>
        {!isDurationValid && (
          <ErrorMessage>
            {intl.formatMessage(
              {
                id: "hebergement.calendar.error",
              },
              {
                maxDays: leadTimeMin,
              }
            )}
          </ErrorMessage>
        )}
      </Column>
    </form>
  );
};
